import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  requestPasswordReset: (email) => api.post('/auth/request-password-reset', { email }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, new_password: newPassword }),
  adminResetPassword: (userId, newPassword) => api.post('/auth/admin-reset-password', { user_id: userId, new_password: newPassword }),
  getMe: () => api.get('/auth/me'),
};

// Scenarios API
export const scenariosAPI = {
  getAll: (params) => api.get('/scenarios', { params }),
  get: (id) => api.get(`/scenarios/${id}`),
  getById: (id) => api.get(`/scenarios/${id}`),
  create: (data) => api.post('/scenarios', data),
  update: (id, data) => api.put(`/scenarios/${id}`, data),
  delete: (id) => api.delete(`/scenarios/${id}`),
  toggleActive: (id) => api.patch(`/scenarios/${id}/toggle-active`),
};

// Simulations API
export const simulationsAPI = {
  start: (scenarioId, options = {}) => api.post('/simulations/start', { 
    scenario_id: scenarioId, 
    institution_id: options.institutionId,
    clinical_role: options.clinicalRole || 'medico',
    protocol_id: options.protocolId
  }),
  begin: (sessionId) => api.post(`/simulations/${sessionId}/begin`),
  startTreatment: (sessionId) => api.post(`/simulations/${sessionId}/start-treatment`),
  get: (sessionId) => api.get(`/simulations/${sessionId}`),
  updateState: (sessionId, data) => api.post(`/simulations/${sessionId}/update-state`, data),
  evolve: (sessionId) => api.post(`/simulations/${sessionId}/evolve`),
  administerMedication: (sessionId, data) => api.post(`/simulations/${sessionId}/administer-medication`, {
    session_id: sessionId,
    time_elapsed_seconds: data.time_elapsed_seconds,
    ...data
  }),
  performAction: (sessionId, data) => api.post(`/simulations/${sessionId}/perform-action`, {
    session_id: sessionId,
    time_elapsed_seconds: data.time_elapsed_seconds,
    ...data
  }),
  end: (sessionId) => api.post(`/simulations/${sessionId}/end`),
  generateDebriefing: (sessionId) => api.post(`/simulations/${sessionId}/generate-debriefing`),
  getMyQuota: () => api.get('/my/simulation-quota'),
  pause: (sessionId) => api.post(`/simulations/${sessionId}/pause`),
  resume: (sessionId) => api.post(`/simulations/${sessionId}/resume`),
  getDebriefing: (sessionId) => api.get(`/simulations/${sessionId}/debriefing`),
  // Timeline
  getTimeline: (sessionId) => api.get(`/simulations/${sessionId}/timeline`),
  // Timeline Analysis for Debriefing
  getTimelineAnalysis: (sessionId) => api.get(`/simulations/${sessionId}/timeline-analysis`),
  // Scenario-specific medications and actions (filtered by role)
  getAvailableMedications: (sessionId) => api.get(`/simulations/${sessionId}/available-medications`),
  getAvailableActions: (sessionId) => api.get(`/simulations/${sessionId}/available-actions`),
  // Protocol Status & Filtered Elements
  getProtocolStatus: (sessionId) => api.get(`/simulations/${sessionId}/protocol-status`),
  getFilteredMedications: (sessionId) => api.get(`/simulations/${sessionId}/filtered-medications`),
  getFilteredActions: (sessionId) => api.get(`/simulations/${sessionId}/filtered-actions`),
  // Instructor Panel
  instructorIntervene: (sessionId, data) => api.post(`/simulations/${sessionId}/instructor/intervene`, data),
  getInstructorInterventions: (sessionId) => api.get(`/simulations/${sessionId}/instructor/interventions`),
  getInstructorStatus: (sessionId) => api.get(`/simulations/${sessionId}/instructor/status`),
  getClinicalShortcuts: () => api.get('/instructor/clinical-shortcuts'),
};

// Catalog API
export const catalogAPI = {
  getMedications: () => api.get('/medications'),
  getClinicalActions: () => api.get('/clinical-actions'),
  getSpecialties: () => api.get('/specialties'),
  getLabTests: () => api.get('/lab-tests'),
  getImagingStudies: () => api.get('/imaging-studies'),
};

// Scenario Clinical Configuration API
export const scenarioClinicalAPI = {
  getConfig: (scenarioId) => api.get(`/scenarios/${scenarioId}/clinical-config`),
  updateConfig: (scenarioId, config) => api.put(`/scenarios/${scenarioId}/clinical-config`, config),
  migrateConfig: (scenarioId) => api.post(`/scenarios/${scenarioId}/migrate-clinical-config`),
  migrateAll: () => api.post('/scenarios/migrate-all-clinical-config'),
};

// Medications CRUD API
export const medicationsAPI = {
  getAll: (includeInactive = false) => api.get('/medications', { params: { include_inactive: includeInactive } }),
  create: (data) => api.post('/medications', data),
  update: (id, data) => api.put(`/medications/${id}`, data),
  toggleActive: (id) => api.patch(`/medications/${id}/toggle-active`),
  delete: (id) => api.delete(`/medications/${id}`),
};

// Clinical Actions CRUD API
export const clinicalActionsAPI = {
  getAll: (includeInactive = false) => api.get('/clinical-actions', { params: { include_inactive: includeInactive } }),
  create: (data) => api.post('/clinical-actions', data),
  update: (id, data) => api.put(`/clinical-actions/${id}`, data),
  toggleActive: (id) => api.patch(`/clinical-actions/${id}/toggle-active`),
  delete: (id) => api.delete(`/clinical-actions/${id}`),
};

// Administration Routes (Vías de Administración) API
export const administrationRoutesAPI = {
  getAll: (includeInactive = false) => api.get('/clinical-maintainers/administration-routes', { params: { include_inactive: includeInactive } }),
  create: (data) => api.post('/clinical-maintainers/administration-routes', data),
  update: (id, data) => api.put(`/clinical-maintainers/administration-routes/${id}`, data),
  toggleActive: (id) => api.patch(`/clinical-maintainers/administration-routes/${id}/toggle-active`),
  delete: (id) => api.delete(`/clinical-maintainers/administration-routes/${id}`),
};

// Medication Presentations (Presentaciones) API
export const medicationPresentationsAPI = {
  getAll: (includeInactive = false) => api.get('/clinical-maintainers/medication-presentations', { params: { include_inactive: includeInactive } }),
  create: (data) => api.post('/clinical-maintainers/medication-presentations', data),
  update: (id, data) => api.put(`/clinical-maintainers/medication-presentations/${id}`, data),
  toggleActive: (id) => api.patch(`/clinical-maintainers/medication-presentations/${id}/toggle-active`),
  delete: (id) => api.delete(`/clinical-maintainers/medication-presentations/${id}`),
};

// Patient Interaction API
export const patientAPI = {
  askAnamnesis: (sessionId, data) => api.post(`/simulations/${sessionId}/anamnesis`, data),
  performPhysicalExam: (sessionId, data) => api.post(`/simulations/${sessionId}/physical-exam`, data),
  orderLabTests: (sessionId, tests) => api.post(`/simulations/${sessionId}/lab-tests`, { tests }),
  orderImaging: (sessionId, studies) => api.post(`/simulations/${sessionId}/imaging`, { studies }),
};

// PDF Report API
export const reportAPI = {
  downloadPDF: (sessionId) => api.get(`/simulations/${sessionId}/report/pdf`, { responseType: 'blob' }),
};

// Institutions API
export const institutionsAPI = {
  getAll: (params) => api.get('/institutions', { params }),
  getById: (id) => api.get(`/institutions/${id}`),
  create: (data) => api.post('/institutions', data),
  update: (id, data) => api.put(`/institutions/${id}`, data),
  delete: (id) => api.delete(`/institutions/${id}`),
  toggleActive: (id) => api.patch(`/institutions/${id}/toggle-active`),
  getTypes: () => api.get('/institutions/types'),
  getRegions: () => api.get('/institutions/regions'),
  getStats: () => api.get('/institutions/stats'),
  getUsers: (id, params) => api.get(`/institutions/${id}/users`, { params }),
  assignAdmin: (instId, userId) => api.post(`/institutions/${instId}/assign-admin`, null, { params: { user_id: userId } }),
  getSubscription: (id) => api.get(`/institutions/${id}/subscription`),
  assignPlan: (id, data) => api.put(`/institutions/${id}/subscription`, data),
  getSubscriptionHistory: (id) => api.get(`/institutions/${id}/subscription/history`),
};

// Plans API (SaaS)
export const plansAPI = {
  getAll: (params) => api.get('/plans', { params }),
  getById: (id) => api.get(`/plans/${id}`),
  getPublic: () => api.get('/plans/public'),
};

// Menu Preferences API
export const menuPreferencesAPI = {
  get: () => api.get('/menu-preferences'),
  update: (data) => api.put('/menu-preferences', data),
  reset: () => api.delete('/menu-preferences'),
};

// Users API
export const usersAPI = {
  getAll: (params) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (userId, data) => api.put(`/users/${userId}`, data),
  updateRole: (userId, role) => api.put(`/users/${userId}/role`, null, { params: { role } }),
  assignTenant: (userId, tenantId) => api.put(`/users/${userId}/tenant`, null, { params: { tenant_id: tenantId } }),
  toggleActive: (userId) => api.patch(`/users/${userId}/toggle-active`),
  delete: (userId) => api.delete(`/users/${userId}`),
  getPermissions: (userId) => api.get(`/users/${userId}/permissions`),
  getPending: () => api.get('/users/pending'),
};

// Roles API
export const rolesAPI = {
  getAll: () => api.get('/roles'),
  getById: (id) => api.get(`/roles/${id}`),
  create: (data) => api.post('/roles', data),
  update: (id, data) => api.put(`/roles/${id}`, data),
  delete: (id) => api.delete(`/roles/${id}`),
};

// OSCE / Evaluations API
export const osceAPI = {
  // Exams
  getExams: () => api.get('/osce/exams'),
  getExamById: (id) => api.get(`/osce/exams/${id}`),
  createExam: (data) => api.post('/osce/exams', data),
  updateExam: (id, data) => api.put(`/osce/exams/${id}`, data),
  deleteExam: (id) => api.delete(`/osce/exams/${id}`),
  // Sessions
  getSessions: () => api.get('/osce/sessions'),
  getSessionById: (id) => api.get(`/osce/sessions/${id}`),
  createSession: (data) => api.post('/osce/sessions', data),
  updateSession: (id, data) => api.put(`/osce/sessions/${id}`, data),
  startSession: (id) => api.post(`/osce/sessions/${id}/start`),
  endSession: (id) => api.post(`/osce/sessions/${id}/end`),
  // Results
  submitStationResult: (sessionId, stationId, data) => api.post(`/osce/sessions/${sessionId}/stations/${stationId}/result`, data),
  getSessionResults: (sessionId) => api.get(`/osce/sessions/${sessionId}/results`),
};

// Clinical Protocols API
export const protocolsAPI = {
  // Protocols CRUD
  getAll: (params) => api.get('/protocols', { params }),
  getById: (id) => api.get(`/protocols/${id}`),
  create: (data) => api.post('/protocols', data),
  update: (id, data) => api.put(`/protocols/${id}`, data),
  delete: (id) => api.delete(`/protocols/${id}`),
  // Protocol details
  getMedications: (protocolId, role) => api.get(`/protocols/${protocolId}/medications`, { params: { role } }),
  getActions: (protocolId, role) => api.get(`/protocols/${protocolId}/actions`, { params: { role } }),
  // Clinical roles
  getClinicalRoles: () => api.get('/clinical-roles'),
};

// Stats API
export const statsAPI = {
  getDashboard: () => api.get('/stats/dashboard'),
  getUserHistory: (userId) => api.get(`/stats/user/${userId}/history`),
};

// Simulation Levels API
export const simulationLevelsAPI = {
  // Info
  getLevelsInfo: () => api.get('/simulation-levels/info'),
  
  // Institution levels
  getInstitutionLevels: (institutionId) => api.get(`/institutions/${institutionId}/simulation-levels`),
  updateInstitutionLevels: (institutionId, niveles) => api.put(`/institutions/${institutionId}/simulation-levels`, null, { params: { niveles } }),
  
  // Groups
  getGroups: (institutionId) => api.get(`/institutions/${institutionId}/simulation-groups`),
  createGroup: (institutionId, data) => api.post(`/institutions/${institutionId}/simulation-groups`, null, { params: data }),
  updateGroup: (groupId, data) => api.put(`/simulation-groups/${groupId}`, null, { params: data }),
  deleteGroup: (groupId) => api.delete(`/simulation-groups/${groupId}`),
  
  // User levels
  getUserLevels: (userId) => api.get(`/users/${userId}/simulation-levels`),
  assignUserLevels: (userId, data) => api.post(`/users/${userId}/simulation-levels`, data),
  bulkAssignLevels: (data) => api.post('/users/bulk-assign-simulation-levels', null, { params: data }),
  
  // Audit
  getAuditLog: (institutionId, limit = 50) => api.get(`/institutions/${institutionId}/simulation-levels/audit`, { params: { limit } }),
  
  // Access status
  getAccessStatus: () => api.get('/scenarios/access-status'),
};

// Courses API
export const coursesAPI = {
  getAll: (params) => api.get('/courses', { params }),
  get: (id) => api.get(`/courses/${id}`),
  create: (data) => api.post('/courses', data),
  update: (id, data) => api.put(`/courses/${id}`, data),
  delete: (id) => api.delete(`/courses/${id}`),
  
  // Estudiantes en curso
  getStudents: (courseId) => api.get(`/courses/${courseId}/students`),
  addStudents: (courseId, studentIds) => api.post(`/courses/${courseId}/students`, studentIds),
  removeStudent: (courseId, studentId) => api.delete(`/courses/${courseId}/students/${studentId}`),
  
  // Co-instructores
  addCoInstructor: (courseId, instructorId) => api.post(`/courses/${courseId}/co-instructors?instructor_id=${instructorId}`),
  removeCoInstructor: (courseId, instructorId) => api.delete(`/courses/${courseId}/co-instructors/${instructorId}`),
  
  // Escenarios
  updateScenarios: (courseId, scenarioIds) => api.put(`/courses/${courseId}/scenarios`, scenarioIds),
};

// Instructor-Students API (Supervision)
export const instructorStudentsAPI = {
  // Mis estudiantes
  getMyStudents: (params) => api.get('/instructor-students/my-students', { params }),
  getStudentDetail: (studentId) => api.get(`/instructor-students/my-students/${studentId}`),
  
  // Asignaciones
  assignStudent: (data) => api.post('/instructor-students/assign', data),
  assignBulk: (data) => api.post('/instructor-students/assign-bulk', data),
  unassignStudent: (studentId) => api.delete(`/instructor-students/unassign/${studentId}`),
  
  // Sesiones activas (para supervisión en vivo)
  getActiveSessions: (params) => api.get('/instructor-students/active-sessions', { params }),
  
  // Dashboard metrics
  getDashboardMetrics: () => api.get('/instructor-students/dashboard-metrics'),
  
  // Permisos de supervisión
  canSupervise: (studentId) => api.get(`/instructor-students/can-supervise/${studentId}`),
  
  // Estudiantes disponibles para asignar
  getAvailableStudents: (params) => api.get('/instructor-students/available-students', { params }),
};

// Supervision API (WebSocket support)
export const supervisionAPI = {
  // Información de sesión WebSocket
  getSessionInfo: (sessionId) => api.get(`/supervision/session/${sessionId}/info`),
  
  // Mis sesiones supervisadas activas
  getMySupervisedSessions: () => api.get('/supervision/my-sessions'),
  
  // Iniciar/finalizar supervisión
  startSupervision: (sessionId, mode = 'observador') => 
    api.post(`/supervision/session/${sessionId}/start?mode=${mode}`),
  endSupervision: (sessionId, notes) => 
    api.post(`/supervision/session/${sessionId}/end`, { notes }),
  
  // Mensajes de sesión
  getSessionMessages: (sessionId, limit = 50) => 
    api.get(`/supervision/session/${sessionId}/messages?limit=${limit}`),
  
  // WebSocket URL helper
  getWebSocketUrl: (sessionId, token, mode = 'instructor') => {
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = BACKEND_URL.replace(/^https?:\/\//, '');
    // IMPORTANTE: usar /api/ws/ para que el proxy redirija al backend
    return `${wsProtocol}//${wsHost}/api/ws/simulation/${sessionId}?token=${token}&mode=${mode}`;
  }
};

// Supervision API V2 (Enhanced)
export const supervisionV2API = {
  // Estudiantes autorizados con estados
  getAuthorizedStudents: (params) => api.get('/supervision/v2/authorized-students', { params }),
  
  // Estado completo de sesión
  getSessionFullState: (sessionId) => api.get(`/supervision/v2/session/${sessionId}/full-state`),
  
  // Control de vitales
  modifyVitals: (sessionId, data) => api.post(`/supervision/v2/session/${sessionId}/modify-vitals`, data),
  
  // Eventos clínicos
  activateEvent: (sessionId, data) => api.post(`/supervision/v2/session/${sessionId}/activate-event`, data),
  getAvailableEvents: (scenarioId) => api.get('/supervision/v2/available-events', { params: { scenario_id: scenarioId } }),
  
  // Mensajes
  sendMessage: (sessionId, data) => api.post(`/supervision/v2/session/${sessionId}/send-message`, data),
  
  // Control de simulación
  pauseSimulation: (sessionId, reason) => api.post(`/supervision/v2/session/${sessionId}/pause`, { reason }),
  extendTime: (sessionId, minutes) => api.post(`/supervision/v2/session/${sessionId}/extend-time`, { minutes }),
  resumeSimulation: (sessionId) => api.post(`/supervision/v2/session/${sessionId}/resume`),
  endSimulation: (sessionId, notes) => api.post(`/supervision/v2/session/${sessionId}/end-simulation`, { notes }),
  
  // Notas del instructor
  addNote: (sessionId, data) => api.post(`/supervision/v2/session/${sessionId}/add-note`, data),
  
  // Control multi-instructor
  requestControl: (sessionId) => api.post(`/supervision/v2/session/${sessionId}/request-control`),
  getActiveInstructors: (sessionId) => api.get(`/supervision/v2/session/${sessionId}/active-instructors`),
  
  // Límites de supervisión
  getLimits: () => api.get('/supervision/v2/limits'),
  canSupervise: (sessionId) => api.get(`/supervision/v2/can-supervise/${sessionId}`),
  
  // Historial de supervisiones
  getHistory: (params) => api.get('/supervision/v2/history', { params }),
  getSupervisionDetail: (supervisionId) => api.get(`/supervision/v2/history/${supervisionId}`),
  getStats: (period = 'month') => api.get('/supervision/v2/stats', { params: { period } }),
};

// Centro de Carga Masiva
const downloadBlob = async (url, params) => {
  const response = await api.get(url, { params, responseType: 'blob' });
  const disposition = response.headers['content-disposition'] || '';
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : 'archivo.xlsx';
  const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(blobUrl);
};

export const bulkAPI = {
  getModules: () => api.get('/bulk/modules'),
  downloadTemplate: (moduleKey, withExamples = false) =>
    downloadBlob(`/bulk/template/${moduleKey}`, { with_examples: withExamples }),
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/bulk/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getJob: (jobId) => api.get(`/bulk/jobs/${jobId}`),
  executeImport: (jobId, types, duplicateMode) =>
    api.post(`/bulk/jobs/${jobId}/import`, { types, duplicate_mode: duplicateMode }),
  getHistory: (params) => api.get('/bulk/history', { params }),
  downloadReport: (jobId) => downloadBlob(`/bulk/jobs/${jobId}/report`),
  downloadOriginal: (jobId) => downloadBlob(`/bulk/jobs/${jobId}/original`),
  exportModule: (moduleKey, params) => downloadBlob(`/bulk/export/${moduleKey}`, params),
  getVersions: (moduleKey, codigo) => api.get(`/bulk/versions/${moduleKey}`, { params: { codigo } }),
  setValidationState: (moduleKey, itemId, estado, observaciones) =>
    api.patch(`/bulk/${moduleKey}/${itemId}/validation-state`, { estado, observaciones }),
  getPharmaPresentations: (params) => api.get('/bulk/pharma-presentations', { params }),
  // Bibliotecas compartidas
  publishLibrary: (data) => api.post('/bulk/libraries/publish', data),
  getLibraries: () => api.get('/bulk/libraries'),
  importLibrary: (libraryId, duplicateMode = 'omitir') =>
    api.post(`/bulk/libraries/${libraryId}/import`, { duplicate_mode: duplicateMode }),
  deleteLibrary: (libraryId) => api.delete(`/bulk/libraries/${libraryId}`),
};

// ==================== Landing Demo Requests (v10.6.1 mejorado) ====================
export const landingDemoAPI = {
  listRequests: (params) => api.get('/landing-demo/requests', { params }),
  updateRequest: (id, data) => api.patch(`/landing-demo/requests/${id}`, data),
  deleteRequest: (id) => api.delete(`/landing-demo/requests/${id}`),
  exportCsvUrl: (params) => {
    const qs = new URLSearchParams(params || {}).toString();
    return `${API_URL}/landing-demo/requests/export.csv${qs ? `?${qs}` : ''}`;
  },
};

// ==================== Physiological Effects (v10.6.0 Fase 2) ====================
export const physiologicalEffectsAPI = {
  // Definitions
  list: (institutionId) =>
    api.get('/physiological-effects', { params: institutionId ? { institution_id: institutionId } : {} }),
  create: (data) => api.post('/physiological-effects', data),
  get: (id) => api.get(`/physiological-effects/${id}`),
  update: (id, data) => api.put(`/physiological-effects/${id}`, data),
  delete: (id) => api.delete(`/physiological-effects/${id}`),

  // Links - Medication
  listMedicationLinks: (medicationId) =>
    api.get(`/physiological-effects/links/medication/${medicationId}`),
  linkMedication: (data) => api.post('/physiological-effects/links/medication', data),
  unlinkMedication: (linkId) => api.delete(`/physiological-effects/links/medication/${linkId}`),

  // Links - Action
  listActionLinks: (actionId) => api.get(`/physiological-effects/links/action/${actionId}`),
  linkAction: (data) => api.post('/physiological-effects/links/action', data),
  unlinkAction: (linkId) => api.delete(`/physiological-effects/links/action/${linkId}`),

  // Overrides
  listScenarioOverrides: (scenarioId) =>
    api.get(`/physiological-effects/overrides/scenario/${scenarioId}`),
  createOverride: (data) => api.post('/physiological-effects/overrides', data),
  deleteOverride: (overrideId) => api.delete(`/physiological-effects/overrides/${overrideId}`),

  // Active effects for a session
  getActiveForSession: (sessionId) =>
    api.get(`/physiological-effects/session/${sessionId}/active`),
};

export default api;
